import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Loader2,
  Plus,
  Calculator,
  Check,
  Send,
  FileText,
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  Download,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { LiveProcessingButton } from "../components/LiveProcessingButton";
import { UnderConstructionDialog } from "../components/UnderConstructionDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Payroll = () => {
  const { token, isOfficeManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [calculatedPayroll, setCalculatedPayroll] = useState(null);
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [calculating, setCalculating] = useState(false);
  
  // New payroll dialog
  const [newPayrollDialogOpen, setNewPayrollDialogOpen] = useState(false);
  const [newPayrollData, setNewPayrollData] = useState({
    start_date: "",
    end_date: "",
    pay_date: ""
  });
  // Phase 19.7: gate the LIVE bank-dispatch step behind UC dialog
  const [processLiveUcOpen, setProcessLiveUcOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [runsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/payroll/periods`, { headers }),
        axios.get(`${API}/payroll/settings`, { headers })
      ]);
      setPayrollRuns(runsRes.data || []);
      setSettings(settingsRes.data || {});
    } catch (error) {
      toast.error("Failed to load payroll data");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayroll = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/payroll/periods/generate`, newPayrollData, { headers });
      setPayrollRuns(prev => [res.data, ...prev]);
      setSelectedRun(res.data);
      setNewPayrollDialogOpen(false);
      toast.success("Payroll period generated");
    } catch (error) {
      toast.error("Failed to generate payroll");
    } finally {
      setGenerating(false);
    }
  };

  const handleCalculatePayroll = async (periodId) => {
    try {
      setCalculating(true);
      const res = await axios.post(`${API}/payroll/periods/${periodId}/calculate`, {}, { headers });
      setCalculatedPayroll(res.data);
      
      // Update the selected run with calculated values
      if (selectedRun && selectedRun.id === periodId) {
        setSelectedRun(prev => ({
          ...prev,
          entries: res.data.entries
        }));
      }
      
      toast.success("Payroll calculated");
    } catch (error) {
      toast.error("Failed to calculate payroll");
    } finally {
      setCalculating(false);
    }
  };

  const handleUpdateEntry = (entryId, field, value) => {
    if (!selectedRun) return;
    
    setSelectedRun(prev => ({
      ...prev,
      entries: prev.entries.map(entry => 
        entry.id === entryId 
          ? { ...entry, [field]: value, hours_overridden: true }
          : entry
      )
    }));
  };

  const handleSavePayroll = async () => {
    if (!selectedRun) return;
    
    try {
      await axios.put(`${API}/payroll/periods/${selectedRun.id}`, {
        entries: selectedRun.entries,
        notes: selectedRun.notes
      }, { headers });
      toast.success("Payroll saved");
    } catch (error) {
      toast.error("Failed to save payroll");
    }
  };

  const handleApprovePayroll = async () => {
    if (!selectedRun) return;
    
    try {
      await axios.post(`${API}/payroll/periods/${selectedRun.id}/approve`, {}, { headers });
      setSelectedRun(prev => ({ ...prev, status: "approved" }));
      setPayrollRuns(prev => prev.map(r => 
        r.id === selectedRun.id ? { ...r, status: "approved" } : r
      ));
      toast.success("Payroll approved");
    } catch (error) {
      toast.error("Failed to approve payroll");
    }
  };

  const handleProcessPayroll = async () => {
    // Phase 19.7: Live bank dispatch is gated as an Under Construction add-on.
    // This handler is kept for compatibility but the UI no longer calls it directly —
    // the "Dispatch Direct Deposit (LIVE)" button opens a UC dialog instead.
    setProcessLiveUcOpen(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: "secondary",
      processing: "outline",
      approved: "default",
      paid: "default"
    };
    const colors = {
      draft: "bg-gray-100 text-gray-800",
      processing: "bg-yellow-100 text-yellow-800",
      approved: "bg-blue-100 text-blue-800",
      paid: "bg-green-100 text-green-800"
    };
    return <Badge className={colors[status] || ""}>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="payroll-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payroll</h1>
          <p className="text-muted-foreground">
            In-house timesheet generation, tax calculation &amp; approval
          </p>
        </div>
        {isOfficeManager && (
          <div className="flex items-center gap-2">
            <LiveProcessingButton featureName="Payroll" dataTestId="payroll-live-btn" />
            <Button onClick={() => setNewPayrollDialogOpen(true)} data-testid="new-payroll-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Payroll Run
            </Button>
          </div>
        )}
      </div>

      {/* Phase 19.7: Clarify what works in-house vs the LIVE add-on */}
      <Card className="border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-900/40" data-testid="payroll-mode-banner">
        <CardContent className="pt-4 pb-4 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 mt-0.5">
            <Calculator className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-emerald-900 dark:text-emerald-100">
              In-House Processing Active
            </p>
            <p className="text-sm text-emerald-800/90 dark:text-emerald-200/80 mt-0.5">
              Generate timesheets, auto-pull hours from schedules, calculate CPP/EI/federal/provincial
              taxes, edit entries, save &amp; approve runs, and export CSV/PDF — all locally.
              <span className="font-medium"> Direct deposit / bank dispatch</span> is an optional
              <span className="font-medium"> LIVE Add-on</span> (currently Under Construction) — click
              "Switch to LIVE Processing" above to join the waitlist.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pay Frequency</p>
                <p className="text-xl font-bold capitalize">{settings?.frequency || "Biweekly"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Payroll</p>
                <p className="text-xl font-bold">
                  {payrollRuns.find(r => r.status === "paid")?.period?.pay_date || "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Runs</p>
                <p className="text-xl font-bold">
                  {payrollRuns.filter(r => r.status === "draft").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Processing Mode</p>
                <p className="text-xl font-bold capitalize">{settings?.processing_mode || "Internal"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payroll Runs List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Payroll Periods</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {payrollRuns.length === 0 ? (
              <p className="text-muted-foreground text-sm">No payroll runs yet</p>
            ) : (
              payrollRuns.map(run => (
                <div
                  key={run.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedRun?.id === run.id ? "border-primary bg-primary/5" : "hover:bg-muted"
                  }`}
                  onClick={() => setSelectedRun(run)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {run.period?.start_date} - {run.period?.end_date}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Pay Date: {run.period?.pay_date}
                      </p>
                    </div>
                    {getStatusBadge(run.status)}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Selected Payroll Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {selectedRun ? `Payroll: ${selectedRun.period?.start_date} - ${selectedRun.period?.end_date}` : "Select a Payroll Period"}
                </CardTitle>
                {selectedRun && (
                  <CardDescription>
                    Pay Date: {selectedRun.period?.pay_date} • {selectedRun.entries?.length || 0} employees
                  </CardDescription>
                )}
              </div>
              {selectedRun && (
                <div className="flex gap-2">
                  {selectedRun.status === "draft" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCalculatePayroll(selectedRun.id)}
                        disabled={calculating}
                      >
                        {calculating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Calculator className="h-4 w-4 mr-1" />}
                        Calculate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSavePayroll}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleApprovePayroll}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                    </>
                  )}
                  {selectedRun.status === "approved" && (
                    <Button
                      size="sm"
                      onClick={() => setProcessLiveUcOpen(true)}
                      data-testid="dispatch-live-payment-btn"
                      className="border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-300"
                      variant="outline"
                      title="Direct deposit dispatch — requires the LIVE Payroll add-on"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Dispatch Direct Deposit (LIVE)
                    </Button>
                  )}
                  {selectedRun.status === "paid" && (
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-1" />
                      Export Report
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedRun ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a payroll period to view details</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Employee Entries Table */}
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="text-right">Reg Hrs</TableHead>
                        <TableHead className="text-right">OT Hrs</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Deductions</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-center">Auto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedRun.entries || []).map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">{entry.employee_name}</TableCell>
                          <TableCell className="text-right">
                            {selectedRun.status === "draft" ? (
                              <Input
                                type="number"
                                step="0.5"
                                className="w-20 text-right h-8"
                                value={entry.regular_hours || 0}
                                onChange={(e) => handleUpdateEntry(entry.id, "regular_hours", parseFloat(e.target.value) || 0)}
                              />
                            ) : (
                              entry.regular_hours?.toFixed(1)
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {selectedRun.status === "draft" ? (
                              <Input
                                type="number"
                                step="0.5"
                                className="w-20 text-right h-8"
                                value={entry.overtime_hours || 0}
                                onChange={(e) => handleUpdateEntry(entry.id, "overtime_hours", parseFloat(e.target.value) || 0)}
                              />
                            ) : (
                              entry.overtime_hours?.toFixed(1)
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(entry.hourly_rate)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(entry.gross_pay)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            -{formatCurrency(entry.total_deductions)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(entry.net_pay)}
                          </TableCell>
                          <TableCell className="text-center">
                            {entry.auto_pulled_hours ? (
                              <Badge variant="outline" className="text-xs">Auto</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Manual</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Summary */}
                {calculatedPayroll && selectedRun.id === calculatedPayroll.period_id && (
                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Employees</p>
                          <p className="text-2xl font-bold">{calculatedPayroll.summary.employee_count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Gross</p>
                          <p className="text-2xl font-bold">{formatCurrency(calculatedPayroll.summary.total_gross)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Deductions</p>
                          <p className="text-2xl font-bold text-red-600">{formatCurrency(calculatedPayroll.summary.total_deductions)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Net</p>
                          <p className="text-2xl font-bold text-green-600">{formatCurrency(calculatedPayroll.summary.total_net)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Payroll Dialog */}
      <Dialog open={newPayrollDialogOpen} onOpenChange={setNewPayrollDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Payroll Run</DialogTitle>
            <DialogDescription>
              Set the pay period dates. Employee hours will be auto-populated from schedules.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start Date</Label>
                <Input
                  type="date"
                  value={newPayrollData.start_date}
                  onChange={(e) => setNewPayrollData({ ...newPayrollData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Period End Date</Label>
                <Input
                  type="date"
                  value={newPayrollData.end_date}
                  onChange={(e) => setNewPayrollData({ ...newPayrollData, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pay Date</Label>
              <Input
                type="date"
                value={newPayrollData.pay_date}
                onChange={(e) => setNewPayrollData({ ...newPayrollData, pay_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPayrollDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGeneratePayroll} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Generate Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 19.7: LIVE direct-deposit dispatch is Under Construction */}
      <UnderConstructionDialog
        open={processLiveUcOpen}
        onOpenChange={setProcessLiveUcOpen}
        featureName="LIVE Payroll Direct Deposit Dispatch"
      />
    </div>
  );
};

export default Payroll;
